// ✅ Fichier : ChatSidebar.js

import React, { useEffect, useState, useCallback } from 'react';
import SockJS from 'sockjs-client';
import { Client } from '@stomp/stompjs';
import '../css/ChatSidebar.css';
import { FaCircle } from 'react-icons/fa';

function ChatSidebar({ onSelectUser, refreshTrigger, selectedUser }) {
  const [contacts, setContacts] = useState([]);
  const [selectedId, setSelectedId] = useState(null);


  const sortContacts = useCallback(
    (
      list,
      selectedUserValue = selectedUser
    ) => {

      const contactsCopy =
        [...list];

      contactsCopy.sort(
        (a, b) => {

          if (
            String(a.id) ===
            String(selectedUserValue?.id)
          ) {
            return -1;
          }

          if (
            String(b.id) ===
            String(selectedUserValue?.id)
          ) {
            return 1;
          }

          return (
            new Date(
              b.lastMessageTime || 0
            ) -
            new Date(
              a.lastMessageTime || 0
            )
          );
        }
      );

      return contactsCopy;
    },
    [selectedUser]
  );

  useEffect(() => {

    if (selectedUser?.id) {

      setSelectedId(
        selectedUser.id
      );

    }

  }, [selectedUser?.id]);

  useEffect(() => {
    const fetchContacts = async () => {
      const token = localStorage.getItem('authToken');
      try {
        const res = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/contacts`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        const data = await res.json();
        let updated = [...data];

        if (selectedUser?.id) {

          const exists =
            updated.some(
              contact =>
                String(contact.id) ===
                String(selectedUser.id)
            );

          if (!exists) {
            updated.unshift(
              selectedUser
            );
          }
        }

        setContacts(
          sortContacts(
            updated,
            selectedUser
          )
        );
      } catch (err) {
        console.error("Failed to refresh contacts", err);
      }
    };

    const handler = () => fetchContacts();

    window.addEventListener('refresh-contacts', handler);
    return () => window.removeEventListener('refresh-contacts', handler);
  }, [selectedUser,
    sortContacts]);


  useEffect(() => {
    const userJson = localStorage.getItem("currentUser");
    const user = userJson ? JSON.parse(userJson) : null;
    const token = localStorage.getItem('authToken');

    const socket = new SockJS(`${process.env.REACT_APP_API_URL}/ws?token=${token}`);
    const client = new Client({
      webSocketFactory: () => socket,
      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },
      onConnect: () => {
        console.log("✅ WebSocket connected (ChatSidebar)");
        if (user) {
          client.publish({
            destination: "/app/presence",
            body: JSON.stringify({ userId: user.id, status: "online" })
          });
        }

        client.subscribe("/topic/presence", (msg) => {
          const presence = JSON.parse(msg.body);
          setContacts(prev =>
            prev.map(c =>
              String(c.id) ===
                String(presence.userId)
                ? {
                  ...c,
                  online:
                    presence.status === "online"
                }
                : c
            )
          );
        });
      },
      onStompError: (frame) => console.error("STOMP error", frame)
    });

    client.activate();

    return () => {
      if (user && client.connected) {
        client.publish({
          destination: "/app/presence",
          body: JSON.stringify({ userId: user.id, status: "offline" })
        });
      }
      setTimeout(() => client.deactivate(), 300);
    };
  }, []);


  useEffect(() => {

    if (!selectedUser?.id) {
      return;
    }


    setContacts(prev => {

      const exists =
        prev.some(
          contact =>
            String(contact.id) ===
            String(selectedUser.id)
        );


      let updated =
        exists
          ? prev.map(
            contact =>
              String(contact.id) ===
                String(selectedUser.id)
                ? {
                  ...contact,
                  ...selectedUser
                }
                : contact
          )
          : [
            selectedUser,
            ...prev
          ];


      return sortContacts(
        updated,
        selectedUser
      );

    });

  }, [selectedUser, sortContacts]);

  useEffect(() => {
    const token = localStorage.getItem('authToken');
    fetch(`${process.env.REACT_APP_API_URL}/api/chat/contacts`, {
      headers: {
        Authorization: `Bearer ${token}`
      }
    })
      .then(res => res.json())

      .then(data => {

        let updated = [...data];

        // Si la personne sélectionnée n'est pas encore
        // dans /api/chat/contacts, on la garde quand même.
        if (selectedUser?.id) {

          const exists = updated.some(
            contact =>
              String(contact.id) ===
              String(selectedUser.id)
          );

          if (!exists) {
            updated.unshift(selectedUser);
          }
        }

        setContacts(
          sortContacts(
            updated,
            selectedUser
          )
        );

      })
      .catch(err => console.error("Failed to load contacts", err));
  }, [refreshTrigger, selectedUser, sortContacts]);

  const handleClick = async (contact) => {
    setSelectedId(contact.id);
    onSelectUser(contact);

    const token = localStorage.getItem('authToken');
    try {
      await fetch(`${process.env.REACT_APP_API_URL}/api/chat/mark-as-read/${contact.id}`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      // ✅ Refresh la liste des contacts après avoir marqué comme lu
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/chat/contacts`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      const updated =
        await res.json();

      let contactsToShow =
        [...updated];

      const selectedContact =
        contact;

      const exists =
        contactsToShow.some(
          item =>
            String(item.id) ===
            String(selectedContact.id)
        );

      if (!exists) {

        contactsToShow.unshift(
          selectedContact
        );
      }

      setContacts(
        sortContacts(
          contactsToShow,
          selectedContact
        )
      );

      // ✅ Déclenche la mise à jour de la navbar (badge message)
      window.dispatchEvent(new Event("refresh-notifications"));

    } catch (err) {
      console.error("❌ Failed to mark as read or refresh contacts", err);
    }
  };





  return (
    <div className="chat-sidebar">
      {contacts.map(contact => (
        <div
          key={contact.id}
          className={`chat-contact ${String(selectedId) ===
            String(contact.id)
            ? 'selected'
            : ''
            }`}
          onClick={() => handleClick(contact)}

        >
          <div className="contact-avatar-wrapper">
            <img
              className="contact-avatar"
              src={contact.mediaUrls?.[0] || '/default-profile.png'}
              alt={contact.name}
            />
            <span className={`contact-status ${contact.online ? 'online' : 'offline'}`}>
              <FaCircle />
            </span>
            {contact.unreadCount > 0 && (
              <span className="unread-badge">{contact.unreadCount}</span>
            )}
          </div>

        </div>
      ))}
    </div>
  );
}

export default ChatSidebar;
